import React, { useState } from 'react';
import { ChatPanel } from '@/components/Chat/ChatPanel';
import { useConversationList, useCreateConversation, useDeleteConversation } from '@/hooks/useConversations';
import type { Conversation } from '@/services/conversationService';
import { formatTime, formatDate, getWeekdayLabels } from '@/lib/localeFormatters';

const PROVIDERS = [
  { value: 'openai',    label: 'OpenAI',    defaultModel: 'gpt-4o-mini' },
  { value: 'anthropic', label: 'Anthropic', defaultModel: 'claude-sonnet-4-20250514' },
  { value: 'gemini',    label: 'Gemini',    defaultModel: 'gemini-2.0-flash' },
];

const MODELS: Record<string, { value: string; label: string }[]> = {
  openai: [
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
    { value: 'gpt-4o',      label: 'GPT-4o' },
    { value: 'gpt-5',       label: 'GPT-5' },
  ],
  anthropic: [
    { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4' },
    { value: 'claude-haiku-35-20241022', label: 'Claude Haiku 3.5' },
    { value: 'claude-opus-4-20250514',   label: 'Claude Opus 4' },
  ],
  gemini: [
    { value: 'gemini-2.0-flash',      label: 'Gemini 2.0 Flash' },
    { value: 'gemini-2.5-flash',      label: 'Gemini 2.5 Flash' },
    { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite' },
  ],
};

function formatChatDate(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diffHrs = (now.getTime() - d.getTime()) / (1000 * 60 * 60);
  if (diffHrs < 24)  return formatTime(d);
  if (diffHrs < 168) return getWeekdayLabels()[d.getDay()];
  return formatDate(d);
}

export function ChatPage() {
  const [activeId, setActiveId]     = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [provider, setProvider]     = useState('openai');
  const [model, setModel]           = useState('gpt-4o-mini');
  const [title, setTitle]           = useState('');

  const { data, isLoading }  = useConversationList({ limit: 50 });
  const createMutation       = useCreateConversation();
  const deleteMutation       = useDeleteConversation();

  const conversations: Conversation[] = data?.items ?? [];
  const active = conversations.find((c) => c.id === activeId) ?? null;

  function handleProviderChange(p: string) {
    setProvider(p);
    setModel(PROVIDERS.find((pr) => pr.value === p)?.defaultModel ?? '');
  }

  async function handleCreate() {
    const conv = await createMutation.mutateAsync({
      provider,
      model,
      title: title.trim() || undefined,
    });
    setActiveId(conv.id);
    setShowNewForm(false);
    setTitle('');
  }

  async function handleDelete(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    if (!confirm('Delete this conversation?')) return;
    await deleteMutation.mutateAsync(id);
    if (activeId === id) setActiveId(null);
  }

  return (
    <div className="flex h-full overflow-hidden">

      {/* ── Left sidebar ── */}
      <div className="w-72 flex-shrink-0 border-r border-border bg-background flex flex-col h-full">

        {/* Header */}
        <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Conversations</h2>
            <p className="text-[11px] text-muted-foreground mt-0.5">Multi-turn AI chat</p>
          </div>
          <button
            onClick={() => setShowNewForm((v) => !v)}
            className="flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-gray-900 text-white dark:bg-white dark:text-gray-900 hover:opacity-80 transition-opacity"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            New
          </button>
        </div>

        {/* New conversation form */}
        {showNewForm && (
          <div className="px-3 py-3 border-b border-border bg-muted/40 space-y-2 flex-shrink-0">
            <input
              type="text"
              placeholder="Title (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              className="w-full text-xs px-2.5 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <select
              value={provider}
              onChange={(e) => handleProviderChange(e.target.value)}
              className="w-full text-xs px-2.5 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {PROVIDERS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full text-xs px-2.5 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {(MODELS[provider] ?? []).map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <div className="flex gap-2 pt-0.5">
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending}
                className="flex-1 text-xs font-medium bg-gray-900 text-white dark:bg-white dark:text-gray-900 px-2.5 py-2 rounded-lg hover:opacity-80 disabled:opacity-40 transition-opacity"
              >
                {createMutation.isPending ? 'Starting…' : 'Start Chat'}
              </button>
              <button
                onClick={() => setShowNewForm(false)}
                className="text-xs px-2.5 py-2 rounded-lg border border-border text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Conversation list — scrollable */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-xs text-muted-foreground">Loading…</div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 px-4 text-center">
              <p className="text-xs text-muted-foreground">No conversations yet.</p>
              <button
                onClick={() => setShowNewForm(true)}
                className="mt-2 text-xs font-medium text-foreground underline underline-offset-2 hover:opacity-70"
              >
                Start one →
              </button>
            </div>
          ) : (
            conversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => setActiveId(conv.id)}
                className={[
                  'w-full text-left px-4 py-3 border-b border-border/50 hover:bg-muted/50 transition-colors group relative',
                  activeId === conv.id ? 'bg-muted border-l-2 border-l-gray-900 dark:border-l-white' : '',
                ].join(' ')}
              >
                <div className="flex items-start justify-between gap-2 pr-5">
                  <span className="text-xs font-medium text-foreground truncate">
                    {conv.title || `${conv.provider} / ${conv.model.split('-')[0]}`}
                  </span>
                  <span className="text-[10px] text-muted-foreground flex-shrink-0 mt-px">
                    {conv.last_message_at ? formatChatDate(conv.last_message_at) : formatChatDate(conv.created_at)}
                  </span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5 capitalize">
                  {conv.provider} · {conv.message_count} msg{conv.message_count !== 1 ? 's' : ''}
                  {parseFloat(conv.total_cost_usd) > 0 && ` · $${parseFloat(conv.total_cost_usd).toFixed(4)}`}
                </p>
                <button
                  onClick={(e) => handleDelete(e, conv.id)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 rounded text-muted-foreground hover:text-red-500 transition-all"
                  title="Delete"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </button>
            ))
          )}
        </div>
      </div>

      {/* ── Main chat area ── */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        {active ? (
          <>
            {/* Chat header bar */}
            <div className="px-5 py-2.5 border-b border-border bg-background flex items-center gap-3 flex-shrink-0">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-foreground truncate">
                  {active.title || 'Untitled Chat'}
                </h3>
                <p className="text-[11px] text-muted-foreground capitalize">
                  {active.provider} · {active.model}
                </p>
              </div>
              <div className="ml-auto flex items-center gap-4 text-[11px] text-muted-foreground flex-shrink-0">
                <span>{active.message_count} messages</span>
                {parseFloat(active.total_cost_usd) > 0 && (
                  <span>${parseFloat(active.total_cost_usd).toFixed(4)}</span>
                )}
              </div>
            </div>

            {/* ChatPanel fills remaining height */}
            <div className="flex-1 min-h-0 overflow-hidden">
              <ChatPanel conversationId={active.id} />
            </div>
          </>
        ) : (
          /* Empty state */
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-foreground mb-1">Start a conversation</h3>
            <p className="text-sm text-muted-foreground max-w-xs mb-5">
              Chat with OpenAI, Anthropic, or Gemini. Responses stream in real-time.
            </p>
            <button
              onClick={() => setShowNewForm(true)}
              className="flex items-center gap-2 bg-gray-900 text-white dark:bg-white dark:text-gray-900 px-4 py-2 rounded-lg hover:opacity-80 transition-opacity text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
              New Chat
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
